import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useLoadingContext } from '@/hooks/useLoadingContext';
import { useUserValidation } from '@/features/user/hooks/useUserValidation';

export type ProfileView = 'profile' | 'tickets' | 'favourites' | 'settings' | 'purchases';

export interface UseProfileViewReturn {
    activeView: ProfileView;
    handleViewChange: (view: ProfileView) => void;
    validationError: string | null;
    shouldRedirect: boolean;
}

export const useProfileView = (
    onTicketsView?: () => void,
    onPurchasesView?: () => void,
    onFavouritesView?: () => void
): UseProfileViewReturn => {
    const { isAuthenticated, isGuest, isLoading: authLoading } = useAuth();
    const { startLoading, stopLoading } = useLoadingContext();
    const { validateUser, error: validationError } = useUserValidation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Track if initial fetch was done
    const initialFetchDoneRef = useRef(false);

    // Get initial view from URL (only once on mount)
    const getInitialView = (): ProfileView => {
        const viewParam = searchParams.get('view');
        if (viewParam && ['profile', 'tickets', 'favourites', 'settings', 'purchases'].includes(viewParam)) {
            return viewParam as ProfileView;
        }
        return 'profile';
    };

    const [activeView, setActiveView] = useState<ProfileView>(getInitialView);
    const [shouldRedirect, setShouldRedirect] = useState(false);

    // Handle view change (user-initiated)
    const handleViewChange = useCallback((view: ProfileView) => {
        setActiveView(view);
        // Update URL parameter
        if (view !== 'profile') {
            setSearchParams({ view });
        } else {
            setSearchParams({});
        }

        // Trigger callbacks for specific views
        if (view === 'tickets' && onTicketsView) {
            onTicketsView();
        }
        if (view === 'purchases' && onPurchasesView) {
            onPurchasesView();
        }
        if (view === 'favourites' && onFavouritesView) {
            onFavouritesView();
        }
    }, [setSearchParams, onTicketsView, onPurchasesView, onFavouritesView]);

    // Handle initial load - only fetch once on mount if starting on tickets/purchases/favourites
    useEffect(() => {
        if (initialFetchDoneRef.current) return;

        if (activeView === 'tickets' && onTicketsView) {
            onTicketsView();
            initialFetchDoneRef.current = true;
        }
        if (activeView === 'purchases' && onPurchasesView) {
            onPurchasesView();
            initialFetchDoneRef.current = true;
        }
        if (activeView === 'favourites' && onFavouritesView) {
            onFavouritesView();
            initialFetchDoneRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeView]);

    // Redirect unauthenticated or guest users to home
    useEffect(() => {
        if (!authLoading && (!isAuthenticated || isGuest)) {
            navigate('/');
            setShouldRedirect(true);
        }
    }, [authLoading, isAuthenticated, isGuest, navigate]);

    // Validate user when authenticated
    useEffect(() => {
        if (isAuthenticated) {
            startLoading('profile-validation');
            validateUser()
                .catch(() => {
                    // Error handling is done in the hook
                })
                .finally(() => {
                    stopLoading('profile-validation');
                });
        }
    }, [isAuthenticated, validateUser, startLoading, stopLoading]);

    return {
        activeView,
        handleViewChange,
        validationError,
        shouldRedirect,
    };
};
